import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ToastContainer, toast } from "react-toastify";
import { Bell } from "lucide-react";
import "react-toastify/dist/ReactToastify.css";
import Sidebar from "./components/Sidebar";
import Header from "./components/Header";
import WelcomeOnboardingModal from "./components/WelcomeOnboardingModal";
import Dashboard from "./pages/Dashboard";
import Perfil from "./pages/Perfil";
import Agendamentos from "./pages/Agendamentos";
import Profissionais from "./pages/Profissionais";
import Servicos from "./pages/Servicos";
import Estabelecimentos from "./pages/Estabelecimentos";
import NovoEstabelecimento from "./pages/NovoEstabelecimento";
import Avaliacoes from "./pages/Avaliacoes";
import Configuracoes from "./pages/Configuracoes/Configuracoes";
import Assinatura from "./pages/Assinatura";
import AssinaturaGuard from "./components/AssinaturaGuard";
import SubscriptionBanner from "./components/SubscriptionBanner";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import PrimeiroAcesso from "./pages/PrimeiroAcesso";
import { sessionManager } from "./utils/sessionManager";
import api from "./api/api";
import { initSocket, closeSocket, syncSocketEstabelecimento } from "./realtime/socket";
import { ProfissionalRotuloProvider } from "./contexts/ProfissionalRotuloContext";
import "./index.css";

const PageTransition = ({ children }) => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -15 }}
        transition={{ duration: 0.25 }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
};

const Layout = ({ children }) => {
  const location = useLocation();
  const isLoginPage = ["/login", "/esqueci-senha", "/resetar-senha", "/reset-senha", "/primeiro-acesso"].includes(location.pathname);
  const isSuperEstabelecimentoNovo = location.pathname === "/super/estabelecimentos/novo";

  if (isLoginPage || isSuperEstabelecimentoNovo) return <>{children}</>;

  return (
    <ProfissionalRotuloProvider>
      <div className="app-shell">
        <Sidebar />
        <main className="main">
          <Header />
          <SubscriptionBanner />
          <div className="container fade-in">{children}</div>
          <WelcomeOnboardingModal />
        </main>
      </div>
    </ProfissionalRotuloProvider>
  );
};

// Componente que escuta eventos globais de sessão e redireciona
const AuthListener = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => {
      const reason = e?.detail?.reason || "";
      const query = reason === "expired" || reason === "refresh_expired" ? "?sessionExpired=true" : "";
      navigate(`/login${query}`, { replace: true });
    };

    window.addEventListener("session:ended", handler);
    // Also poll server token validity as a fallback when token exists.
    const interval = setInterval(async () => {
      if (document.hidden) return;
      const token = sessionManager.obterToken();
      if (!token) return;
      try {
        await api.get('/usuarios/me');
        // OK - token valid
      } catch (err) {
        // If server says unauthorized or forbidden, clear session and redirect
        const status = err.response?.status;
        if (status === 401 || status === 403) {
          sessionManager.limparSessao();
          navigate('/login?sessionExpired=true', { replace: true });
        }
      }
    }, 60000);

    return () => {
      window.removeEventListener("session:ended", handler);
      clearInterval(interval);
    };
  }, [navigate]);

  return null;
};

const RedirectIfAuthenticated = ({ children }) => {
  if (sessionManager.estaAutenticado()) {
    return <Navigate to={sessionManager.obterDestinoPosLogin()} replace />;
  }
  return children;
};

// Wrapper para rotas que requerem autenticação
const RequireAuth = ({ children }) => {
  const [autenticado, setAutenticado] = useState(() => sessionManager.estaAutenticado());
  const location = useLocation();

  useEffect(() => {
    const cur = sessionManager.estaAutenticado();
    setAutenticado(cur);

    const listener = () => {
      setAutenticado(sessionManager.estaAutenticado());
    };
    window.addEventListener("session:ended", listener);
    return () => window.removeEventListener("session:ended", listener);
  }, []);

  if (!autenticado) {
    return <Navigate to="/login" replace />;
  }

  const usuario = sessionManager.obterUsuario();
  const precisaTrocarSenha = usuario?.precisa_trocar_senha;
  if (precisaTrocarSenha && location.pathname !== "/primeiro-acesso") {
    return <Navigate to="/primeiro-acesso" replace />;
  }

  return <AssinaturaGuard>{children}</AssinaturaGuard>;
};

function App() {
  const [sessionReady, setSessionReady] = useState(false);
  const [autenticado, setAutenticado] = useState(() => sessionManager.estaAutenticado());

  useEffect(() => {
    let mounted = true;

    const bootstrapSession = async () => {
      sessionManager.iniciar();

      if (sessionManager.estaAutenticado()) {
        if (mounted) {
          setAutenticado(true);
          setSessionReady(true);
        }
        return;
      }

      try {
        const { data } = await api.get("/usuarios/me");
        sessionManager.salvarSessao(data);

        if (data?.estabelecimento_id) {
          localStorage.setItem("estabelecimento_id", String(data.estabelecimento_id));
        }
      } catch (error) {
        if (process.env.NODE_ENV !== "production" && error?.response?.status) {
          console.warn("[App] Sessão por cookie não restaurada:", error.response.status);
        }
      } finally {
        if (mounted) {
          setAutenticado(sessionManager.estaAutenticado());
          setSessionReady(true);
        }
      }
    };

    bootstrapSession();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const syncAuth = () => setAutenticado(sessionManager.estaAutenticado());
    window.addEventListener("session:started", syncAuth);
    window.addEventListener("session:ended", syncAuth);
    return () => {
      window.removeEventListener("session:started", syncAuth);
      window.removeEventListener("session:ended", syncAuth);
    };
  }, []);

  useEffect(() => {
    if (!sessionReady || !autenticado) {
      closeSocket();
      return undefined;
    }

    const socket = initSocket();
    if (!socket) return undefined;

    const handleEstabelecimentoChanged = () => {
      syncSocketEstabelecimento();
    };

    window.addEventListener("fz:estabelecimento:changed", handleEstabelecimentoChanged);

    const showRealtimeToast = (tipo, payload) => {
      let mensagem = "Novas atualizações disponíveis.";
      if (tipo === "agendamento") {
        mensagem = payload?.tipo === "criado"
          ? "Novos agendamentos disponíveis."
          : "Novas atualizações disponíveis.";
      } else if (tipo === "notificacao") {
        mensagem = "Você tem novas notificações.";
      }
      const toastId = "fz-realtime";
      if (!toast.isActive(toastId)) {
        toast.info(
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Bell size={16} />
            <span>{mensagem}</span>
          </div>,
          {
            position: "top-right",
            autoClose: 3500,
            toastId,
          }
        );
      }
    };

    const handleAgendamento = (payload) => {
      showRealtimeToast("agendamento", payload);
      window.dispatchEvent(new CustomEvent("fz:agendamento", { detail: payload }));
    };

    const handleAgendamentoEvent = (payload) => {
      const detalhe = payload?.payload || payload;
      showRealtimeToast("agendamento", { tipo: payload?.tipo || "atualizado", ...detalhe });
      window.dispatchEvent(new CustomEvent("fz:agendamento", { detail: detalhe }));
    };

    const handleNotificacao = (payload) => {
      showRealtimeToast("notificacao", payload);
      window.dispatchEvent(new CustomEvent("fz:notificacao", { detail: payload }));
    };

    const handleNotificacaoNova = (payload) => {
      const detalhe = payload?.payload || payload;
      showRealtimeToast("notificacao", detalhe);
      window.dispatchEvent(new CustomEvent("fz:notificacao", { detail: detalhe }));
    };

    socket.on("agendamento:changed", handleAgendamento);
    socket.on("agendamento:criado", handleAgendamentoEvent);
    socket.on("agendamento:confirmado", handleAgendamentoEvent);
    socket.on("agendamento:cancelado", handleAgendamentoEvent);
    socket.on("agendamento:finalizado", handleAgendamentoEvent);
    socket.on("notificacao:changed", handleNotificacao);
    socket.on("notificacao:nova", handleNotificacaoNova);

    // Garante join da room após autenticação/restauração de cookie.
    syncSocketEstabelecimento();

    return () => {
      window.removeEventListener("fz:estabelecimento:changed", handleEstabelecimentoChanged);
      socket.off("agendamento:changed", handleAgendamento);
      socket.off("agendamento:criado", handleAgendamentoEvent);
      socket.off("agendamento:confirmado", handleAgendamentoEvent);
      socket.off("agendamento:cancelado", handleAgendamentoEvent);
      socket.off("agendamento:finalizado", handleAgendamentoEvent);
      socket.off("notificacao:changed", handleNotificacao);
      socket.off("notificacao:nova", handleNotificacaoNova);
      closeSocket();
    };
  }, [sessionReady, autenticado]);

  if (!sessionReady) {
    return null;
  }

  return (
    <Router>
      <AuthListener />
      <ToastContainer position="top-right" theme="colored" style={{ zIndex: 10000 }} />
      <Layout>
        <PageTransition>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" />} />
            <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
            <Route path="/agendamentos" element={<RequireAuth><Agendamentos /></RequireAuth>} />
            <Route path="/profissionais" element={<RequireAuth><Profissionais /></RequireAuth>} />
            <Route path="/servicos" element={<RequireAuth><Servicos /></RequireAuth>} />
            <Route path="/estabelecimentos" element={<RequireAuth><Estabelecimentos /></RequireAuth>} />
            <Route path="/super/estabelecimentos/novo" element={<RequireAuth><NovoEstabelecimento /></RequireAuth>} />
            <Route path="/configuracoes" element={<RequireAuth><Configuracoes /></RequireAuth>} />
            <Route path="/assinatura" element={<RequireAuth><Assinatura /></RequireAuth>} />
            <Route path="/configuracoes/:estabelecimentoId" element={<Navigate to="/configuracoes" replace />} />
            <Route path="/login" element={<RedirectIfAuthenticated><Login /></RedirectIfAuthenticated>} />
            <Route path="/esqueci-senha" element={<RedirectIfAuthenticated><ForgotPassword /></RedirectIfAuthenticated>} />
            <Route path="/resetar-senha" element={<RedirectIfAuthenticated><ResetPassword /></RedirectIfAuthenticated>} />
            <Route path="/reset-senha" element={<RedirectIfAuthenticated><ResetPassword /></RedirectIfAuthenticated>} />
            <Route path="/primeiro-acesso" element={<RequireAuth><PrimeiroAcesso /></RequireAuth>} />
            <Route path="/avaliacoes" element={<RequireAuth><Avaliacoes /></RequireAuth>} />
            <Route path="/perfil" element={<RequireAuth><Perfil /></RequireAuth>} />
          </Routes>
        </PageTransition>
      </Layout>
    </Router>
  );
}

export default App;
